import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarOverlapOverflow } from '@/components/appointments/CalendarOverlapOverflow';
import type { AppointmentListItem } from '@/hooks/useAppointments';
import {
  WEEKDAY_KEYS,
  isSameDay,
  startOfDay,
  weekdayKeyIndex,
} from '@/utils/appointmentCalendarDates';
import {
  CALENDAR_CLOSED_OVERLAY_CLASS,
  calendarBlockLineVisibility,
  closedHourOverlays,
  getAppointmentCalendarAppearance,
  timedBlockGeometry,
  type CalendarDayHours,
} from '@/utils/appointmentCalendarStyle';
import {
  appointmentToInterval,
  layoutOverlappingAppointments,
  overlapBoxStyle,
  type OverlapPlacement,
} from '@/utils/appointmentOverlapLayout';
import { cn } from '@/utils/cn';

export type TimeGridAppointment = Pick<
  AppointmentListItem,
  | 'id'
  | 'patient_name'
  | 'service_name'
  | 'service_color'
  | 'starts_at'
  | 'ends_at'
  | 'status'
  | 'visit_status'
  | 'payment_status'
  | 'recurrence_pattern'
>;

export function AppointmentTimeGrid({
  days,
  appointments,
  bookingHours,
  hourHeight,
  dayStartHour,
  dayEndHour,
  onAppointmentClick,
  className,
}: {
  days: Date[];
  appointments: TimeGridAppointment[];
  bookingHours: Map<number, CalendarDayHours | null | undefined>;
  hourHeight: number;
  dayStartHour: number;
  dayEndHour: number;
  onAppointmentClick?: (id: string) => void;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => startOfDay(new Date()), []);
  const hours = useMemo(
    () =>
      Array.from({ length: Math.max(1, dayEndHour - dayStartHour) }, (_, i) => dayStartHour + i),
    [dayStartHour, dayEndHour],
  );
  const locale = i18n.language === 'da' ? 'da-DK' : 'en-US';

  const byDay = useMemo(() => {
    const map = new Map<string, TimeGridAppointment[]>();
    for (const appointment of appointments) {
      const dt = new Date(appointment.starts_at);
      const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
      const list = map.get(key) ?? [];
      list.push(appointment);
      map.set(key, list);
    }
    return map;
  }, [appointments]);

  function dayKey(d: Date) {
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }

  return (
    <div
      className={cn(
        'flex h-0 min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-ds-line bg-ds-card shadow-card',
        className,
      )}
    >
      <div
        className="grid shrink-0 border-b border-ds-line"
        style={{ gridTemplateColumns: `64px repeat(${days.length}, minmax(0, 1fr))` }}
      >
        <div className="bg-ds-sunk" />
        {days.map((d) => (
          <div
            key={d.toISOString()}
            className={`border-l border-ds-line px-2 py-2 text-center ${
              isSameDay(d, today)
                ? 'bg-ds-today-col'
                : 'bg-ds-sunk'
            }`}
          >
            <div className="text-xs font-medium text-ds-muted">
              {t(`appointments.weekdays_short.${WEEKDAY_KEYS[weekdayKeyIndex(d)]}`)}
            </div>
            <div
              className={`inline-flex min-w-8 items-center justify-center rounded-full text-base font-semibold ${
                isSameDay(d, today)
                  ? 'bg-ds-teal text-ds-on-accent'
                  : 'text-ds-text'
              }`}
            >
              {d.getDate()}
            </div>
          </div>
        ))}
      </div>

      <div className="h-0 min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div
          className="relative grid"
          style={{
            gridTemplateColumns: `64px repeat(${days.length}, minmax(0, 1fr))`,
            height: hours.length * hourHeight,
          }}
        >
          <div>
            {hours.map((h) => (
              <div
                key={h}
                className="border-b border-ds-line pr-2 text-right text-[11px] text-ds-muted"
                style={{ height: `${hourHeight}px` }}
              >
                <span className="relative -top-1.5">{`${String(h).padStart(2, '0')}:00`}</span>
              </div>
            ))}
          </div>

          {days.map((d) => {
            const dayAppointments = byDay.get(dayKey(d)) ?? [];
            const overlays = closedHourOverlays({
              dayStartHour,
              dayEndHour,
              hourHeight,
              hours: bookingHours.get(d.getDay()),
            });
            return (
              <div
                key={d.toISOString()}
                className="relative h-full border-l border-ds-line"
              >
                {overlays.map((overlay, idx) => (
                  <div
                    key={`${d.toISOString()}-${idx}`}
                    data-testid="closed-hours-overlay"
                    className={`pointer-events-none absolute left-0 right-0 ${CALENDAR_CLOSED_OVERLAY_CLASS}`}
                    style={{ top: `${overlay.top}px`, height: `${overlay.height}px`, zIndex: 0 }}
                  />
                ))}
                {hours.map((h) => (
                  <div
                    key={h}
                    className="border-b border-ds-line"
                    style={{ height: `${hourHeight}px` }}
                  />
                ))}
                <div className="pointer-events-none absolute inset-0 z-[1]">
                  <TimeGridDayEvents
                    appointments={dayAppointments}
                    language={locale}
                    hourHeight={hourHeight}
                    dayStartHour={dayStartHour}
                    dayEndHour={dayEndHour}
                    onAppointmentClick={onAppointmentClick}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TimeGridDayEvents({
  appointments,
  language,
  hourHeight,
  dayStartHour,
  dayEndHour,
  onAppointmentClick,
}: {
  appointments: TimeGridAppointment[];
  language: string;
  hourHeight: number;
  dayStartHour: number;
  dayEndHour: number;
  onAppointmentClick?: (id: string) => void;
}) {
  const overlapLayout = layoutOverlappingAppointments(appointments.map(appointmentToInterval));
  const appointmentById = new Map(appointments.map((appointment) => [appointment.id, appointment]));

  return (
    <>
      {appointments.map((appointment) => {
        const overlapPlacement = overlapLayout.placements.get(appointment.id);
        if (!overlapPlacement || overlapPlacement.hidden) return null;
        return (
          <TimeGridAppointmentBlock
            key={appointment.id}
            appointment={appointment}
            overlapPlacement={overlapPlacement}
            language={language}
            hourHeight={hourHeight}
            dayStartHour={dayStartHour}
            dayEndHour={dayEndHour}
            onClick={onAppointmentClick ? () => onAppointmentClick(appointment.id) : undefined}
          />
        );
      })}
      {overlapLayout.overflowGroups.map((group) => {
        const overflowAppointments = group.ids
          .map((id) => appointmentById.get(id))
          .filter((item): item is TimeGridAppointment => item != null);
        const geometry = timedBlockGeometry({
          startsAt: new Date(group.startMs),
          endsAt: new Date(group.endMs),
          hourHeight,
          dayStartHour,
          dayEndHour,
        });
        if (!geometry || overflowAppointments.length === 0) return null;
        return (
          <CalendarOverlapOverflow
            key={`overflow-${group.ids.join('-')}`}
            appointments={overflowAppointments}
            top={geometry.top}
            height={geometry.height}
            language={language}
            onOpenAppointment={onAppointmentClick ?? (() => undefined)}
          />
        );
      })}
    </>
  );
}

function TimeGridAppointmentBlock({
  appointment,
  overlapPlacement,
  language,
  hourHeight,
  dayStartHour,
  dayEndHour,
  onClick,
}: {
  appointment: TimeGridAppointment;
  overlapPlacement: OverlapPlacement;
  language: string;
  hourHeight: number;
  dayStartHour: number;
  dayEndHour: number;
  onClick?: () => void;
}) {
  const { t } = useTranslation();
  const start = new Date(appointment.starts_at);
  const end = new Date(appointment.ends_at);
  const geometry = timedBlockGeometry({
    startsAt: start,
    endsAt: end,
    hourHeight,
    dayStartHour,
    dayEndHour,
  });
  if (!geometry) return null;

  const { background, accent, statusKey } = getAppointmentCalendarAppearance(appointment);
  const { showTime, showService } = calendarBlockLineVisibility(geometry.height);
  const formatter = new Intl.DateTimeFormat(language, {
    hour: '2-digit',
    minute: '2-digit',
  });
  const timeLabel = `${formatter.format(start)} – ${formatter.format(end)}`;
  const statusLabel = t(`appointments.legend.${statusKey}`);

  return (
    <div
      data-testid="calendar-appointment"
      data-appointment-id={appointment.id}
      style={{
        top: `${geometry.top}px`,
        height: `${geometry.height}px`,
        background,
        borderLeft: `3px solid ${accent}`,
        zIndex: 1,
        ...overlapBoxStyle(overlapPlacement),
      }}
      className={`pointer-events-auto overflow-hidden rounded-lg text-left text-[11px] leading-tight text-ds-text shadow-card ring-1 ring-ds-line-strong ${
        onClick ? 'cursor-pointer hover:brightness-[0.98]' : ''
      }`}
      onClick={onClick}
      title={[appointment.patient_name, timeLabel, statusLabel, appointment.service_name]
        .filter(Boolean)
        .join(' · ')}
    >
      <div
        className={geometry.height < 34 ? 'min-w-0 px-1 pb-1 pt-0.5' : 'min-w-0 px-1.5 pb-2 pt-1'}
      >
        <div className="truncate font-semibold leading-none">{appointment.patient_name}</div>
        {showTime && (
          <div className="mt-0.5 truncate text-[10px] text-ds-muted">
            {timeLabel}
          </div>
        )}
        {showService && appointment.service_name && (
          <div className="mt-0.5 truncate text-[10px] text-ds-muted">
            {appointment.service_name}
          </div>
        )}
      </div>
    </div>
  );
}
